// /frontend/src/pages/dashboard/post/components/SchedulePickerModal.js  (v2.0 – modernized UI)
// All logic identical to v1.
import React, { useState, useEffect } from 'react'
import { Spinner } from 'react-bootstrap'
import axios from 'axios'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import moment from 'moment'
import { getPlatformIcon } from '../../accounts/helpers/platformIcons'
import 'react-big-calendar/lib/css/react-big-calendar.css'

const localizer = momentLocalizer(moment)

const CALENDAR_CSS = `
  .spm-cal .rbc-toolbar button {
    border: 1.5px solid #e5e7eb !important;
    border-radius: 8px !important;
    color: #374151 !important;
    font-size: 13px !important;
    font-weight: 600 !important;
    padding: 6px 14px !important;
    background: #ffffff !important;
    transition: all 0.15s !important;
  }
  .spm-cal .rbc-toolbar button:hover {
    border-color: #2563eb !important;
    color: #2563eb !important;
    background: #eff6ff !important;
  }
  .spm-cal .rbc-toolbar button.rbc-active {
    background: #2563eb !important;
    color: #ffffff !important;
    border-color: #2563eb !important;
    box-shadow: none !important;
  }
  .spm-cal .rbc-toolbar-label {
    font-size: 15px !important;
    font-weight: 800 !important;
    color: #111827 !important;
  }
  .spm-cal .rbc-header {
    border-bottom: 1px solid #f3f4f6 !important;
    padding: 10px 0 !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.07em !important;
    color: #9ca3af !important;
  }
  .spm-cal .rbc-today { background: #eff6ff !important; }
  .spm-cal .rbc-off-range-bg { background: #fafafa !important; }
  .spm-cal .rbc-day-bg { border-left: 1px solid #f3f4f6 !important; }
  .spm-cal .rbc-month-row { border-top: 1px solid #f3f4f6 !important; overflow: visible !important; }
  .spm-cal .rbc-date-cell { font-size: 12px !important; font-weight: 600 !important; color: #6b7280 !important; padding: 6px 8px !important; }
  .spm-cal .rbc-date-cell.rbc-now { color: #2563eb !important; font-weight: 800 !important; }
  .spm-cal .rbc-time-slot { font-size: 11px !important; color: #9ca3af !important; }
  .spm-cal .rbc-timeslot-group { border-bottom: 1px solid #f3f4f6 !important; min-height: 44px !important; }
  .spm-cal .rbc-current-time-indicator { background: #2563eb !important; height: 2px !important; }
  .spm-cal .rbc-slot-selection { background: rgba(37,99,235,0.1) !important; border: 1px solid #2563eb !important; }
`

export default function SchedulePickerModal ({ show = false, onHide = () => {}, onDateSelect = () => {} }) {
  const [events,  setEvents]  = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const style = document.createElement('style')
    style.innerHTML = CALENDAR_CSS
    document.head.appendChild(style)
    return () => document.head.removeChild(style)
  }, [])

  useEffect(() => {
    if (!show) return
    ;(async () => {
      try {
        setLoading(true)
        const { data } = await axios.get('/api/posting/all-posts', { withCredentials: true })
        const mapped = data.map(row => {
          const start = new Date(row.post_date)
          const end   = new Date(start)
          end.setMinutes(end.getMinutes() + 60)
          return { id: row.id, title: '', start, end, allDay: false, mediaUrl: row.media_url, userAvatar: row.user_avatar, platformIcon: getPlatformIcon(row.platform) }
        })
        setEvents(mapped)
      } catch (err) {
        console.error('[SchedulePickerModal] fetch error:', err)
      } finally {
        setLoading(false)
      }
    })()
  }, [show])

  const handleSlot = ({ start }) => { onDateSelect(start); onHide() }

  const tileSize = 50
  function EventComponent ({ event }) {
    const isVideo = /\.(mp4|mov|m4v|avi|wmv)$/i.test(event.mediaUrl || '')
    return (
      <div style={{ width:tileSize, height:tileSize, position:'relative', borderRadius:8, overflow:'hidden', background:'#f3f4f6', boxShadow:'0 1px 3px rgba(0,0,0,0.1)' }}>
        {event.mediaUrl && (
          isVideo
            ? <video src={event.mediaUrl} muted preload='metadata' style={{ width:'100%', height:'100%', objectFit:'cover' }} />
            : <div style={{ width:'100%', height:'100%', backgroundImage:`url("${encodeURI(event.mediaUrl)}")`, backgroundSize:'cover', backgroundPosition:'center' }} />
        )}
        {event.userAvatar && (
          <img src={event.userAvatar} alt='' style={{ position:'absolute', bottom:2, left:2, width:14, height:14, borderRadius:'50%', border:'1.5px solid #fff', objectFit:'cover' }} />
        )}
        {event.platformIcon && (
          <i className={event.platformIcon} style={{ position:'absolute', bottom:2, right:2, fontSize:11, color:'#fff', textShadow:'0 0 3px rgba(0,0,0,.6)' }} />
        )}
      </div>
    )
  }

  function eventStyleGetter () {
    return { style: { background:'transparent', border:'none', boxShadow:'none', overflow:'visible' } }
  }

  if (!show) return null

  return (
    <>
      {/* Backdrop */}
      <div onClick={onHide} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1050, backdropFilter:'blur(2px)' }} />

      {/* Full-screen panel */}
      <div style={{
        position    : 'fixed',
        inset       : 0,
        zIndex      : 1051,
        background  : '#f8fafc',
        display     : 'flex',
        flexDirection:'column',
      }}>
        {/* Header */}
        <div style={{
          display        : 'flex',
          alignItems     : 'center',
          justifyContent : 'space-between',
          padding        : '16px 24px',
          background     : '#ffffff',
          borderBottom   : '1px solid #e5e7eb',
          boxShadow      : '0 1px 3px rgba(0,0,0,0.05)',
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:32, height:32, borderRadius:'50%', background:'#eff6ff', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <i className='fas fa-calendar-plus' style={{ fontSize:13, color:'#2563eb' }} />
            </div>
            <div>
              <h3 style={{ fontSize:16, fontWeight:800, color:'#111827', margin:0 }}>Select a Date & Time</h3>
              <p style={{ fontSize:12, color:'#6b7280', margin:0 }}>Click any open slot to schedule your post there.</p>
            </div>
          </div>
          <button onClick={onHide} style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:8, border:'1.5px solid #e5e7eb', background:'transparent', color:'#374151', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            <i className='fas fa-times' style={{ fontSize:11 }} /> Cancel
          </button>
        </div>

        {/* Calendar body */}
        <div style={{ flex:1, overflow:'hidden', padding:20 }}>
          {loading ? (
            <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'100%' }}>
              <Spinner animation='border' style={{ color:'#2563eb' }} />
            </div>
          ) : (
            <div className='spm-cal' style={{
              height      : '100%',
              background  : '#ffffff',
              borderRadius: 12,
              border      : '1px solid #e5e7eb',
              overflow    : 'hidden',
              padding     : 16,
              boxShadow   : '0 1px 3px rgba(0,0,0,0.05)',
            }}>
              <Calendar
                localizer={localizer}
                events={events}
                startAccessor='start'
                endAccessor='end'
                selectable
                onSelectSlot={handleSlot}
                defaultView='week'
                views={['month', 'week', 'day', 'agenda']}
                step={30}
                timeslots={2}
                style={{ height:'100%', border:'none' }}
                components={{ event: EventComponent }}
                eventPropGetter={eventStyleGetter}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}